import { useCallback, useMemo, useState } from 'react';
import { NestedOptions, getAllCheckedPaths, searchNestedOptionsTree, toggleSetItem } from '../TreeSelect.helpers';

/** Shallow-clone every node in the tree so mutations don't touch the memoized original. */
function cloneNestedOptions(source: NestedOptions): NestedOptions {
  const result: NestedOptions = {};
  for (const [key, node] of Object.entries(source)) {
    result[key] = { path: node.path, checked: node.checked, subOptions: cloneNestedOptions(node.subOptions) };
  }
  return result;
}

type UseOldTreeSelectHandlersParams<T extends string> = {
  value: T[];
  onChange: (value: T[]) => void;
  nestedOptions: NestedOptions;
  descendantMap: Map<string, string[]>;
  selectionMode: 'standard' | 'indication';
  searchOptionByPath: (path: string, nestedObject: NestedOptions) => NestedOptions | undefined;
};

export function useOldTreeSelectHandlers<T extends string>({
  value,
  onChange,
  nestedOptions,
  descendantMap,
  selectionMode,
  searchOptionByPath,
}: UseOldTreeSelectHandlersParams<T>) {
  const [searchInput, setSearchInput] = useState('');
  const [userOpenedBranches, setUserOpenedBranches] = useState<Set<string>>(new Set());

  const searchResults = useMemo(
    () => searchNestedOptionsTree(nestedOptions, searchInput),
    [searchInput, nestedOptions],
  );

  const handleClickIndicationMode = useCallback(
    (optionPath: T) => {
      const newSelected = new Set(value);
      const selecting = !newSelected.has(optionPath);
      const itemsToToggle = [optionPath, ...(descendantMap.get(optionPath) || [])] as T[];

      for (const item of itemsToToggle) {
        if (selecting) newSelected.add(item);
        else newSelected.delete(item);
      }

      onChange(Array.from(newSelected));
    },
    [value, descendantMap, onChange],
  );

  const handleClickOption = useCallback(
    (optionPath: string) => {
      if (selectionMode === 'indication') {
        return handleClickIndicationMode(optionPath as T);
      }

      // Clone the tree so we never mutate the memoized nestedOptions.
      const treeCopy = cloneNestedOptions(nestedOptions);
      const option = searchOptionByPath(optionPath, treeCopy);
      if (!option) return;

      let newOptionCheckState = false;
      Object.values(option).forEach(properties => {
        newOptionCheckState = properties.checked !== true;
      });

      const toggleAllSubOptions = (newState: boolean, nestedOption: NestedOptions) => {
        Object.values(nestedOption).forEach(properties => {
          properties.checked = newState;
          if (Object.keys(properties.subOptions).length > 0) {
            toggleAllSubOptions(newState, properties.subOptions);
          }
        });
      };
      toggleAllSubOptions(newOptionCheckState, option);

      const newValue = getAllCheckedPaths(treeCopy);
      onChange(newValue as T[]);
    },
    [selectionMode, handleClickIndicationMode, nestedOptions, onChange, searchOptionByPath],
  );

  const handleToggleBranch = useCallback(
    (optionPath: string, _currentState: boolean) => setUserOpenedBranches(prev => toggleSetItem(prev, optionPath)),
    [],
  );

  return {
    searchInput,
    setSearchInput,
    searchResults,
    userOpenedBranches,
    setUserOpenedBranches,
    handleClickOption,
    handleToggleBranch,
  };
}
